import {useCallback, useEffect} from "react"
import {calculateMovementLimits} from "../utils/calculationsUtils"
import {CONSTANTS} from "../constants/constants"
import type {CropperHookReturn} from "../types/types"

interface UseZoomProps {
    canvasRef: React.RefObject<HTMLCanvasElement>
    cropper: CropperHookReturn
}

export const useZoom = ({canvasRef, cropper}: UseZoomProps) => {
    const handleZoomChange = useCallback(
        (newZoom: number, mousePosition?: {x: number; y: number}) => {
            if (!cropper || cropper.imageState.status !== "loaded") return

            const zoomFactor = newZoom / 100
            const baseWidth = cropper.imageState.dimensions.width
            const baseHeight = cropper.imageState.dimensions.height

            const zoomedDimensions = {
                width: baseWidth * zoomFactor,
                height: baseHeight * zoomFactor,
            }

            const limits = calculateMovementLimits(
                cropper.imageState.dimensions,
                cropper.cropState.dimensions,
                newZoom
            )

            let newPosition = {
                x: Math.min(
                    limits.maxX,
                    Math.max(limits.minX, cropper.imageState.position.x)
                ),
                y: Math.min(
                    limits.maxY,
                    Math.max(limits.minY, cropper.imageState.position.y)
                ),
            }

            if (mousePosition) {
                const relativeX =
                    (mousePosition.x - cropper.imageState.position.x) /
                    ((baseWidth * cropper.imageState.zoom) / 100)
                const relativeY =
                    (mousePosition.y - cropper.imageState.position.y) /
                    ((baseHeight * cropper.imageState.zoom) / 100)

                newPosition = {
                    x: Math.min(
                        limits.maxX,
                        Math.max(
                            limits.minX,
                            mousePosition.x - relativeX * zoomedDimensions.width
                        )
                    ),
                    y: Math.min(
                        limits.maxY,
                        Math.max(
                            limits.minY,
                            mousePosition.y -
                                relativeY * zoomedDimensions.height
                        )
                    ),
                }
            }

            cropper.setImageState({
                zoom: newZoom,
                position: newPosition,
            })

            cropper.setRenderData({
                position: newPosition,
                dimensions: zoomedDimensions,
            })
        },
        [cropper]
    )

    const handleWheel = useCallback(
        (e: WheelEvent) => {
            e.preventDefault()
            e.stopPropagation()

            if (!cropper || cropper.imageState.status !== "loaded") return

            const canvas = canvasRef.current
            if (!canvas) return

            const rect = canvas.getBoundingClientRect()
            const mousePosition = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            }

            const delta = e.deltaY > 0 ? -15 : 15
            let newZoom
            if (cropper.mode === CONSTANTS.EDITOR_MODES.TILE) {
                newZoom = Math.min(
                    Math.max(
                        CONSTANTS.TILE_ZOOM.MIN,
                        cropper.imageState.zoom + delta
                    ),
                    CONSTANTS.TILE_ZOOM.MAX
                )
            } else {
                newZoom = Math.min(
                    Math.max(
                        CONSTANTS.ZOOM.MIN,
                        cropper.imageState.zoom + delta
                    ),
                    CONSTANTS.ZOOM.MAX
                )
            }

            handleZoomChange(newZoom, mousePosition)
        },
        [cropper, canvasRef, handleZoomChange]
    )

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const wheelHandler = (e: WheelEvent) => handleWheel(e as WheelEvent)
        const options = {passive: false} as EventListenerOptions

        canvas.addEventListener("wheel", wheelHandler, options)

        return () => {
            canvas.removeEventListener("wheel", wheelHandler, options)
        }
    }, [canvasRef, handleWheel])

    return {
        handleZoomChange,
        minZoom:
            cropper?.mode === CONSTANTS.EDITOR_MODES.CROP
                ? CONSTANTS.ZOOM.MIN
                : CONSTANTS.TILE_ZOOM.MIN,
        maxZoom:
            cropper?.mode === CONSTANTS.EDITOR_MODES.CROP
                ? CONSTANTS.ZOOM.MAX
                : CONSTANTS.TILE_ZOOM.MAX,
        step:
            cropper?.mode === CONSTANTS.EDITOR_MODES.CROP
                ? CONSTANTS.ZOOM.STEP
                : CONSTANTS.TILE_ZOOM.STEP,
    }
}
